import { useEffect, useState } from "react";
import {
  StoredColumnConfig,
  loadColumnConfig,
} from "../utils/tableColumns";

export type UseTableColumnsOpts = {
  /** localStorage key. Use the `pref.` prefix so it survives logout. */
  storageKey: string;
  defaultOrder: string[];
  /** Optional pre-`pref.`-prefix key to migrate off of. */
  legacyKey?: string;
  /**
   * Column keys that should start hidden — useful for opt-in columns added
   * to an existing table without disturbing users' current layouts.
   */
  defaultHidden?: string[];
};

/**
 * Owns the persisted column config for one table, plus the draft state used
 * by the "Edit columns" panel. Draft edits are staged and only committed on
 * `applyDraft()` (called from the panel's Apply button).
 */
export const useTableColumns = (opts: UseTableColumnsOpts) => {
  const [config, setConfig] = useState<StoredColumnConfig>(() =>
    loadColumnConfig(
      opts.storageKey,
      opts.defaultOrder,
      opts.legacyKey,
      opts.defaultHidden
    )
  );

  useEffect(() => {
    try {
      localStorage.setItem(opts.storageKey, JSON.stringify(config));
    } catch {
      /* private mode / quota — ignore */
    }
  }, [config, opts.storageKey]);

  const [showPanel, setShowPanel] = useState(false);
  const [draggingKey, setDraggingKey] = useState<string | null>(null);
  const [draft, setDraft] = useState<StoredColumnConfig | null>(null);

  // Snapshot the committed config into a draft whenever the panel opens.
  // The panel edits only the draft; nothing touches `config` until Apply.
  useEffect(() => {
    if (showPanel) {
      setDraft({ order: [...config.order], hidden: [...config.hidden] });
    } else {
      setDraft(null);
      setDraggingKey(null);
    }
  }, [showPanel]);

  const isDraftColHidden = (key: string) =>
    draft?.hidden.includes(key) ?? false;

  const toggleDraftColHidden = (key: string) => {
    setDraft((prev) =>
      prev
        ? {
            ...prev,
            hidden: prev.hidden.includes(key)
              ? prev.hidden.filter((k) => k !== key)
              : [...prev.hidden, key],
          }
        : prev
    );
  };

  const resetDraft = () =>
    setDraft({ order: [...opts.defaultOrder], hidden: [] });

  const reorderDraft = (fromKey: string, toKey: string) => {
    if (!fromKey || fromKey === toKey) return;
    setDraft((prev) => {
      if (!prev) return prev;
      const next = [...prev.order];
      const fromIdx = next.indexOf(fromKey);
      const toIdx = next.indexOf(toKey);
      if (fromIdx < 0 || toIdx < 0) return prev;
      next.splice(fromIdx, 1);
      next.splice(toIdx, 0, fromKey);
      return { ...prev, order: next };
    });
  };

  const applyDraft = () => {
    if (draft) setConfig(draft);
    setShowPanel(false);
  };

  return {
    config,
    setConfig,
    showPanel,
    setShowPanel,
    draft,
    draggingKey,
    setDraggingKey,
    isDraftColHidden,
    toggleDraftColHidden,
    resetDraft,
    reorderDraft,
    applyDraft,
  };
};

export type TableColumnsController = ReturnType<typeof useTableColumns>;
